export type SelectOption = {
  label: string;
  value: string;
  isDisabled?: boolean;
};

export const SELECT_PLACEHOLDER = "--- Select ---";
export const MACRO_PLACEHOLDER = "-- Select macro --";

export const toSelectOptions = (items: string[]): SelectOption[] =>
  items.map((item) => ({ label: item, value: item }));

export const withPlaceholder = (
  items: string[],
  placeholder = SELECT_PLACEHOLDER
): SelectOption[] => [
  { label: placeholder, value: "" },
  ...toSelectOptions(items),
];

export const CASE_ID = "P250850001";
export const CASE_NAME = "Lindsay-Surgical";

export const reportTypeOptions = withPlaceholder([
  "Surgical pathology",
  "Gynecologic cytology",
  "Non-gyn cytology / FNA",
]);

export const siteOptions = withPlaceholder([
  "Cecum",
  "Ascending colon",
  "Right neck",
  "Right upper lobe",
  "Left upper lobe",
  "Other",
]);

export const operationOptions = withPlaceholder([
  "Hemicolectomy",
  "Lobectomy",
  "Biopsy",
  "Excision",
  "Other",
]);

export const grossMacroOptions = withPlaceholder([
  "Right hemicolectomy",
  "Left hemicolectomy",
  "Lobectomy",
]);

export const microMacroOptions = withPlaceholder([
  "See synoptic report",
  "Other",
]);

export const finalMacroOptions = withPlaceholder(
  [],
  MACRO_PLACEHOLDER
);

export const reportMacroOptions = withPlaceholder(
  [],
  MACRO_PLACEHOLDER
);

export const SYNOPTIC_NO_NEEDED_LABEL = "--- No synoptic needed ---";

export const synopticTumorSiteOptions: SelectOption[] = [
  { label: SYNOPTIC_NO_NEEDED_LABEL, value: "" },
  { label: "Colon / Rectum", value: "Colon / Rectum" },
  { label: "Lung", value: "Lung" },
  { label: "Breast", value: "Breast" },
  {
    label: "Prostate (coming soon)",
    value: "Prostate",
    isDisabled: true,
  },
  {
    label: "Skin/Melanoma (coming soon)",
    value: "Skin/Melanoma",
    isDisabled: true,
  },
  {
    label: "Thyroid (coming soon)",
    value: "Thyroid",
    isDisabled: true,
  },
];

export const signingPathologistOptions: SelectOption[] = [
  { label: SELECT_PLACEHOLDER, value: "" },
  { label: "John VanHoose, M.D.", value: "John VanHoose, M.D." },
  { label: "John VanHoose, M.D.", value: "John VanHoose, M.D.-2" },
  { label: "Sara Samadzadeh, M.D.", value: "Sara Samadzadeh, M.D." },
  { label: "Regina Burton, M.D.", value: "Regina Burton, M.D." },
  { label: "Paula Eapen, M.D.", value: "Paula Eapen, M.D." },
  { label: "Brandi Kelly, M.D.", value: "Brandi Kelly, M.D." },
  { label: "Daniel Lalich, M.D.", value: "Daniel Lalich, M.D." },
];

export const reportStatusOptions = withPlaceholder([
  "Original Original",
  "Amended report",
  "Addendum report",
  "Preliminary",
]);

export const ihcMarkerOptions = withPlaceholder([
  "Intact",
  "Loss",
  "Equivocal",
  "Negative",
  "Positive",
  "Not performed",
]);

export const DEFAULT_DIAGNOSIS =
  "RIGHT COLON CECAL MASS: MODERATELY DIFFERENTIATED ADENOCARCINOMA, RESECTION OF PERICOLIC LYMPH NODES, TOTAL 29, NEGATIVE FOR METASTATIC CARCINOMA (0/29). FIBROUS OBLITERATION OF APPENDIX.";

export const DEFAULT_CLINICAL_DATA =
  "e.g. Cecal neoplasm / Malignant neoplasm of up...";

export const DEFAULT_GROSS_DESCRIPTION =
  "SPECIMEN RECEIVED: In formalin; SPECIMEN: Ascending colon, appendix, terminal ileum; PROCEDURE: Right hemicolectomy; SPECIMEN DIMENSIONS: 21.4 cm...";

export const DEFAULT_MICRO_DESCRIPTION =
  "Microscopic evaluation performed. See synoptic report.";

export const DEFAULT_IHC_COMMENT =
  "MISMATCH REPAIR (MMR) IHC PANEL: MLH1, MSH2, MSH6, AND PMS2 ALL SHOW INTACT NUCLEAR EXPRESSION BY IMMUNOHISTOCHEMISTRY. THERE IS NO EVIDENCE OF MISMATCH REPAIR DEFICIENCY IN THIS SPECIMEN.";

export type IhcPanelKey = "mmr" | "her2" | "cytokeratin";

export const IHC_PANELS: {
  key: IhcPanelKey;
  label: string;
  addLabel: string;
  activeClass: string;
  fields: { id: string; label: string }[];
}[] = [
  {
    key: "mmr",
    label: "Mismatch repair (MMR)",
    addLabel: "+ Mismatch repair (MMR)",
    activeClass: "btn-primary",
    fields: [
      { id: "mlh1", label: "MLH1" },
      { id: "msh2", label: "MSH2" },
      { id: "msh6", label: "MSH6" },
      { id: "pms2", label: "PMS2" },
    ],
  },
  {
    key: "her2",
    label: "HER2/ER/PR",
    addLabel: "+ HER2 / ER / PR",
    activeClass: "pathology-ihc-her2-active",
    fields: [
      { id: "her2neu", label: "HER2 neu" },
      { id: "er", label: "ER" },
      { id: "pr", label: "PR" },
    ],
  },
  {
    key: "cytokeratin",
    label: "Cytokeratin panel",
    addLabel: "+ Cytokeratin panel",
    activeClass: "btn-linkedin",
    fields: [
      { id: "ck7", label: "CK7" },
      { id: "ck20", label: "CK20" },
      { id: "ckae", label: "CK AE1/AE3" },
      { id: "gata3", label: "GATA3" },
      { id: "gcdfp", label: "GCDFP" },
    ],
  },
];

export type CassetteSlideRow = {
  id: string;
  slide: string;
  stains: string[];
};

export type CassetteBlock = {
  id: string;
  title: string;
  rows: CassetteSlideRow[];
};

export const INITIAL_CASSETTES: CassetteBlock[] = [
  {
    id: "cass-1",
    title: "Cassette 1",
    rows: [
      { id: "c1-r1", slide: "Slide Surgical 1", stains: ["H&E"] },
      { id: "c1-r2", slide: "Slide Surgical 2", stains: ["PAS-F"] },
      { id: "c1-r3", slide: "Slide Surgical 3", stains: ["GMS-Fungus"] },
    ],
  },
  {
    id: "cass-2",
    title: "Cassette 2",
    rows: [
      { id: "c2-r1", slide: "Slide Surgical 1", stains: ["H&E"] },
      { id: "c2-r2", slide: "Slide Surgical 2", stains: ["PAS-F"] },
    ],
  },
];

export const MAIN_TABS = [
  { key: "result", label: "Result" },
  { key: "synoptic", label: "Synoptic" },
  { key: "ihc", label: "IHC / Ancillary" },
  { key: "images", label: "Images" },
  { key: "block", label: "Block Management" },
  { key: "addenda", label: "Addenda" },
  { key: "signout", label: "Sign-out" },
] as const;

export type MainTabKey = (typeof MAIN_TABS)[number]["key"];

export type ResultPart = {
  id: string;
  label: string;
  site: SelectOption | null;
  operation: SelectOption | null;
  clinicalData: string;
  diagnosis: string;
  grossMacro: SelectOption | null;
  grossDescription: string;
  microMacro: SelectOption | null;
  microDescription: string;
  finalMacro: SelectOption | null;
  finalDescription: string;
  reportMacro: SelectOption | null;
  reportDescription: string;
  comment: string;
};

export const createDefaultResultPart = (
  index: number,
  withSampleData = false
): ResultPart => ({
  id: `result-${Date.now()}-${index}`,
  label: `Result ${String.fromCharCode(64 + index)}`,
  site: withSampleData
    ? { label: "Ascending colon", value: "Ascending colon" }
    : null,
  operation: withSampleData
    ? { label: "Lobectomy", value: "Lobectomy" }
    : null,
  clinicalData: "",
  diagnosis: withSampleData ? DEFAULT_DIAGNOSIS : "",
  grossMacro: null,
  grossDescription: withSampleData ? DEFAULT_GROSS_DESCRIPTION : "",
  microMacro: withSampleData
    ? { label: "See synoptic report", value: "See synoptic report" }
    : null,
  microDescription: withSampleData ? DEFAULT_MICRO_DESCRIPTION : "",
  finalMacro: null,
  finalDescription: "",
  reportMacro: null,
  reportDescription: "",
  comment: "",
});
